import {FC, useCallback} from 'react';
import {Typography} from '@components/Typography';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {Form} from '@components/Form';
import {Button} from '@components/Button';
import {Divider} from '@components/Divider';
import {signIn} from 'next-auth/react';
import {InfoText} from '@components/InfoText';
import {Box} from '@components/Box';
import {Input} from '@liveComponents/Field';
import {useToaster} from '@utils/toast';
import {getLocalizedRoute, routes} from '@utils/routes';
import {useLocale} from '@utils/locale';
import {useRouter} from "next/router";

export type LoginFormProps = {
    onSuccess?: () => any;
    onForgottenPassword?: () => any;
    onRegister?: () => any;
    redirect?: boolean;
    callbackUrl?: string;
};

export const LoginForm: FC<LoginFormProps> = ({redirect, callbackUrl, onSuccess, onRegister, onForgottenPassword}) => {
    const intl = useIntl();
    const toaster = useToaster();
    const locale = useLocale();
    const router = useRouter();
    const processLoginSubmit = useCallback(
        (values, actions) => {
            signIn<'credentials'>('credentials', {
                username: values.username,
                redirect: false,
                password: values.password,
                callbackUrl: callbackUrl ?? getLocalizedRoute(routes.homepage, locale),
            }).then((response) => {
                actions.setSubmitting(false);
                if (response && response.error) {
                    const message =
                        response?.error === 'Invalid credentials'
                            ? intl.formatMessage({defaultMessage: 'Zadaná kombinace jména a hesla není platná'})
                            : intl.formatMessage({defaultMessage: 'Při přihlašování došlo k chybě'});
                    actions.setErrors({password: message});
                } else {
                    toaster.toast({
                        bg: 'success',
                        message: intl.formatMessage({defaultMessage: 'Přihlášení proběhlo úspěšně'}),
                        duration: 5000,
                        header: intl.formatMessage({defaultMessage: 'Úspěch'}),
                        color: 'green',
                    });
                    if (redirect && response?.url) {
                        router.push(response.url);
                    }
                    onSuccess && onSuccess();
                }
            });
        },
        [intl, onSuccess, redirect, callbackUrl, toaster, locale, router],
    );

    return (
        <>
            <Typography as="h2" variant="h4" className="mb-2">
                <FormattedMessage defaultMessage='Přihlaste se' />
            </Typography>
            <Typography as="p" variant="perex" color="grey-dark" className="mb-10">
                <FormattedMessage defaultMessage="Přihlaste se zde" />
            </Typography>

            <Formik
                onSubmit={processLoginSubmit}
                initialValues={{
                    username: '',
                    password: '',
                }}
                validationSchema={Yup.object({
                    username: Yup.string().required(intl.formatMessage({defaultMessage: 'Povinné pole'})),
                    password: Yup.string().required(intl.formatMessage({defaultMessage: 'Povinné pole'})),
                })}
            >
                {(props) => (
                    <Form onSubmit={props.handleSubmit} method={"post"}>
                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label visuallyHidden>
                                <FormattedMessage defaultMessage={'Vaše e-mailová adresa'} />
                            </Form.Label>
                            <Input
                                name={'username'}
                                type={'text'}
                                autoComplete={'email'}
                                placeholder={intl.formatMessage({defaultMessage: 'Vaše e-mailová adresa'})}
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="password">
                            <Form.Label visuallyHidden>
                                <FormattedMessage defaultMessage={'Vaše heslo'} />
                            </Form.Label>
                            <Input
                                name={'password'}
                                type={'password'}
                                autoComplete={'current-password'}
                                placeholder={intl.formatMessage({defaultMessage: 'Vaše heslo'})}
                            />
                        </Form.Group>

                        <Button type={'submit'} className="w-100">
                            <FormattedMessage defaultMessage={'Přihlásit se'} />
                        </Button>
                    </Form>
                )}
            </Formik>

            <Divider className="my-10" color="grey-light">
                <FormattedMessage defaultMessage={'Nebo'} />
            </Divider>

            <Button className="w-100 mb-4" variant="outline-secondary" onClick={() => signIn('facebook', {redirect: false, callbackUrl})}>
                <FormattedMessage defaultMessage={'Pokračovat pomocí Facebooku'} />
            </Button>
            <Button className="w-100" variant="outline-primary" onClick={() => signIn('google', {redirect: false, callbackUrl})}>
                <FormattedMessage defaultMessage={'Pokračovat pomocí Google'} />
            </Button>

            {(onForgottenPassword || onRegister) && (
                <Box className="d-flex justify-content-between mt-4">
                    {onForgottenPassword && (
                        <InfoText iconName={false} className="mb-0">
                            <a role={'button'} onClick={onForgottenPassword} className={'link'}>
                                <FormattedMessage defaultMessage={'Zapomněli jste heslo?'} />
                            </a>
                        </InfoText>
                    )}
                    {onRegister && (
                        <InfoText iconName={false} className="mb-0">
                            <FormattedMessage
                                defaultMessage={'Nemáte účet? <link>Vytvořit nový</link>'}
                                values={{
                                    link: (chunks: any) => (
                                        <a role={'button'} onClick={onRegister} className={'link'}>
                                            {chunks}
                                        </a>
                                    ),
                                }}
                            />
                        </InfoText>
                    )}
                </Box>
            )}
        </>
    );
};
